const VERIFIER_KEY = "pkce_code_verifier";

/**
 * Generates a cryptographically random code verifier (RFC 7636).
 * Returns a URL-safe base64 string of at least 43 characters.
 */
export function generateCodeVerifier() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Computes the S256 code challenge for a given verifier (RFC 7636).
 * Returns BASE64URL(SHA256(ASCII(verifier))).
 * @param {string} verifier
 * @returns {Promise<string>}
 */
export async function generateCodeChallenge(verifier) {
  const encoded = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", encoded);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Persists the code verifier in sessionStorage for retrieval after the
 * IdP redirect callback.
 * @param {string} verifier
 */
export function storeCodeVerifier(verifier) {
  sessionStorage.setItem(VERIFIER_KEY, verifier);
}

/**
 * Reads the code verifier from sessionStorage and immediately removes it
 * (one-time use). Returns null if no verifier is stored.
 * @returns {string|null}
 */
export function consumeCodeVerifier() {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  return verifier;
}

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
